import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiCreditCard, FiKey, FiShield, FiDownload, FiUpload,
  FiTrash2, FiCopy, FiCheck, FiAlertTriangle,
  FiInfo, FiX, FiPlus, FiEdit2, FiSave, FiLock,
  FiHardDrive, FiCpu, FiDatabase
} from 'react-icons/fi';
import secureStorage from '../services/secureStorage';
import './SmartCardBackup.css';

function SmartCardBackup({ onClose }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('cards');
  const [cards, setCards] = useState([]);
  const [yubikeys, setYubikeys] = useState([]);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showAddManual, setShowAddManual] = useState(false);
  const [manualBackup, setManualBackup] = useState({
    name: '',
    type: 'smartcard',
    serialNumber: '',
    issuer: '',
    certificates: [],
    totpSecrets: [],
    notes: ''
  });

  // Load saved backups from encrypted storage
  useEffect(() => {
    const loadBackups = async () => {
      try {
        if (secureStorage.isReady()) {
          const cached = await secureStorage.getItem('smartcard_backups');
          if (Array.isArray(cached)) {
            setBackups(cached);
            return;
          }
        }
      } catch (e) {
        console.warn('Error loading smartcard cache:', e);
      }

      const savedBackups = localStorage.getItem('rfid_smartcard_backups');
      if (savedBackups) {
        try {
          const parsed = JSON.parse(savedBackups);
          setBackups(parsed);
          if (secureStorage.isReady()) {
            await secureStorage.setItem('smartcard_backups', parsed);
            localStorage.removeItem('rfid_smartcard_backups');
          }
        } catch (e) {
          console.error('Error loading backups:', e);
        }
      }
    };

    loadBackups();
  }, []);

  // Save backups to encrypted storage
  const saveBackups = useCallback((newBackups) => {
    setBackups(newBackups);
    if (secureStorage.isReady()) {
      secureStorage.setItem('smartcard_backups', newBackups);
    } else {
      localStorage.setItem('rfid_smartcard_backups', JSON.stringify(newBackups));
    }
  }, []);

  // Check for WebUSB support (for YubiKey)
  const hasWebUSB = 'usb' in navigator;
  
  // Check for Web Smart Card API support (experimental)
  const hasSmartCard = 'smartCard' in navigator;

  // Scan for YubiKeys via WebUSB
  const scanYubiKeys = async () => {
    if (!hasWebUSB) {
      setError(t('smartcard.webUsbNotSupported'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // YubiKey USB vendor ID
      const YUBICO_VENDOR_ID = 0x1050;
      
      const device = await navigator.usb.requestDevice({
        filters: [
          { vendorId: YUBICO_VENDOR_ID }
        ]
      });

      if (device) {
        const yubikey = {
          id: `yubikey-${Date.now()}`,
          name: device.productName || 'YubiKey',
          manufacturer: device.manufacturerName || 'Yubico',
          serialNumber: device.serialNumber || 'Unknown',
          productId: device.productId,
          vendorId: device.vendorId,
          type: getYubiKeyType(device.productId),
          detectedAt: new Date().toISOString()
        };

        setYubikeys(prev => {
          const exists = prev.find(y => y.serialNumber === yubikey.serialNumber);
          if (exists) return prev;
          return [...prev, yubikey];
        });

        setSuccess(t('smartcard.deviceDetected', { name: yubikey.name }));
      }
    } catch (err) {
      if (err.name !== 'NotFoundError') {
        setError(t('smartcard.scanError') + ': ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  // Get YubiKey type from product ID
  const getYubiKeyType = (productId) => {
    const types = {
      0x0010: 'YubiKey Plus',
      0x0110: 'YubiKey NEO',
      0x0111: 'YubiKey NEO-N',
      0x0112: 'YubiKey NEO OTP+U2F',
      0x0113: 'YubiKey NEO U2F',
      0x0114: 'YubiKey NEO OTP+CCID',
      0x0115: 'YubiKey NEO U2F+CCID',
      0x0116: 'YubiKey NEO OTP+U2F+CCID',
      0x0120: 'Security Key by Yubico',
      0x0401: 'YubiKey 4 OTP',
      0x0402: 'YubiKey 4 U2F',
      0x0403: 'YubiKey 4 OTP+U2F',
      0x0404: 'YubiKey 4 CCID',
      0x0405: 'YubiKey 4 OTP+CCID',
      0x0406: 'YubiKey 4 U2F+CCID',
      0x0407: 'YubiKey 4 OTP+U2F+CCID',
      0x0410: 'YubiKey Plus',
      0x0500: 'YubiKey 5 NFC',
      0x0501: 'YubiKey 5 Nano',
      0x0502: 'YubiKey 5C',
      0x0503: 'YubiKey 5C Nano',
      0x0504: 'YubiKey 5C NFC',
      0x0505: 'YubiKey 5Ci',
      0x0506: 'YubiKey Bio',
      0x0507: 'Security Key NFC',
      0x0508: 'YubiKey 5 NFC FIPS',
      0x0509: 'YubiKey 5C NFC FIPS',
      0x050a: 'YubiKey 5Ci FIPS',
      0x050b: 'YubiKey 5 Nano FIPS',
      0x050c: 'YubiKey 5C Nano FIPS'
    };
    return types[productId] || 'YubiKey';
  };

  // Scan for Smart Cards (experimental API)
  const scanSmartCards = async () => {
    if (!hasSmartCard) {
      setError(t('smartcard.smartCardApiNotSupported'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Note: Web Smart Card API is still experimental
      const readers = await navigator.smartCard.getReaders();
      
      for (const reader of readers) {
        const card = await reader.connect();
        if (card) {
          const atr = await card.getATR();
          const cardInfo = {
            id: `card-${Date.now()}`,
            reader: reader.name,
            atr: arrayBufferToHex(atr),
            type: identifyCardType(atr),
            detectedAt: new Date().toISOString()
          };
          
          setCards(prev => [...prev, cardInfo]);
          await card.disconnect();
        }
      }
    } catch (err) {
      setError(t('smartcard.scanError') + ': ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Convert ArrayBuffer to hex string
  const arrayBufferToHex = (buffer) => {
    return Array.from(new Uint8Array(buffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('').toUpperCase();
  };

  // Identify card type from ATR
  const identifyCardType = (atr) => {
    const atrHex = arrayBufferToHex(atr);
    
    // Common ATR patterns
    if (atrHex.includes('4A434F50')) return 'JCOP Card';
    if (atrHex.includes('4D494641')) return 'MIFARE';
    if (atrHex.includes('59554249')) return 'YubiKey (CCID)';
    if (atrHex.includes('474F')) return 'Gemalto';
    if (atrHex.includes('5468616C')) return 'Thales';
    if (atrHex.startsWith('3B')) return 'ISO 7816 Smart Card';
    if (atrHex.startsWith('3F')) return 'ISO 7816 Smart Card (Inverse)';
    
    return 'Unknown Smart Card';
  };

  // Create backup of device
  const createBackup = (device) => {
    const backup = {
      id: `backup-${Date.now()}`,
      deviceId: device.id,
      name: device.name || device.type,
      type: device.vendorId ? 'yubikey' : 'smartcard',
      serialNumber: device.serialNumber || device.atr,
      manufacturer: device.manufacturer,
      productType: device.type,
      metadata: { ...device },
      certificates: [],
      totpSecrets: [],
      notes: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const newBackups = [...backups, backup];
    saveBackups(newBackups);
    setSuccess(t('smartcard.backupCreated', { name: device.name || device.type }));
  };

  // Add manual backup entry
  const addManualBackup = () => {
    if (!manualBackup.name) {
      setError(t('smartcard.nameRequired'));
      return;
    }

    const backup = {
      id: `backup-${Date.now()}`,
      ...manualBackup,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    const newBackups = [...backups, backup];
    saveBackups(newBackups);
    setSuccess(t('smartcard.backupCreated', { name: manualBackup.name }));
    setShowAddManual(false);
    setManualBackup({
      name: '',
      type: 'smartcard',
      serialNumber: '',
      issuer: '',
      certificates: [],
      totpSecrets: [],
      notes: ''
    });
  };

  // Delete backup
  const deleteBackup = (backupId) => {
    if (window.confirm(t('smartcard.confirmDelete'))) {
      const newBackups = backups.filter(b => b.id !== backupId);
      saveBackups(newBackups);
      setSuccess(t('smartcard.backupDeleted'));
    }
  };

  // Add TOTP secret to backup
  const addTotpToBackup = (backupId, totpData) => {
    const newBackups = backups.map(b => {
      if (b.id === backupId) {
        return {
          ...b,
          totpSecrets: [...(b.totpSecrets || []), totpData],
          updatedAt: new Date().toISOString()
        };
      }
      return b;
    });
    saveBackups(newBackups);
  };

  // Add certificate to backup
  const addCertificateToBackup = (backupId, certData) => {
    const newBackups = backups.map(b => {
      if (b.id === backupId) {
        return {
          ...b,
          certificates: [...(b.certificates || []), certData],
          updatedAt: new Date().toISOString()
        };
      }
      return b;
    });
    saveBackups(newBackups);
  };

  // Export all backups
  const exportBackups = () => {
    const exportData = {
      version: '1.0',
      exportedAt: new Date().toISOString(),
      backups: backups
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `smartcard-backups-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setSuccess(t('smartcard.exportSuccess'));
  };

  // Export encrypted backup
  const exportEncryptedBackups = async () => {
    const password = prompt(t('smartcard.enterEncryptionPassword'));
    if (!password) return;

    try {
      const exportData = {
        version: '1.0',
        encrypted: true,
        exportedAt: new Date().toISOString(),
        backups: backups
      };

      // Encrypt with Web Crypto API
      const encoder = new TextEncoder();
      const data = encoder.encode(JSON.stringify(exportData));
      
      // Derive key from password
      const keyMaterial = await crypto.subtle.importKey(
        'raw',
        encoder.encode(password),
        'PBKDF2',
        false,
        ['deriveBits', 'deriveKey']
      );

      const salt = crypto.getRandomValues(new Uint8Array(16));
      const key = await crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: salt,
          iterations: 100000,
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
      );

      const iv = crypto.getRandomValues(new Uint8Array(12));
      const encryptedData = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        key,
        data
      );

      // Combine salt + iv + encrypted data
      const combined = new Uint8Array(salt.length + iv.length + encryptedData.byteLength);
      combined.set(salt, 0);
      combined.set(iv, salt.length);
      combined.set(new Uint8Array(encryptedData), salt.length + iv.length);

      const blob = new Blob([combined], { type: 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartcard-backups-encrypted-${new Date().toISOString().split('T')[0]}.enc`;
      a.click();
      URL.revokeObjectURL(url);
      setSuccess(t('smartcard.encryptedExportSuccess'));
    } catch (err) {
      setError(t('smartcard.encryptionError') + ': ' + err.message);
    }
  };

  // Import backups
  const importBackups = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      if (file.name.endsWith('.enc')) {
        // Encrypted file
        const password = prompt(t('smartcard.enterDecryptionPassword'));
        if (!password) return;

        const arrayBuffer = await file.arrayBuffer();
        const combined = new Uint8Array(arrayBuffer);
        
        const salt = combined.slice(0, 16);
        const iv = combined.slice(16, 28);
        const encryptedData = combined.slice(28);

        const encoder = new TextEncoder();
        const keyMaterial = await crypto.subtle.importKey(
          'raw',
          encoder.encode(password),
          'PBKDF2',
          false,
          ['deriveBits', 'deriveKey']
        );

        const key = await crypto.subtle.deriveKey(
          {
            name: 'PBKDF2',
            salt: salt,
            iterations: 100000,
            hash: 'SHA-256'
          },
          keyMaterial,
          { name: 'AES-GCM', length: 256 },
          false,
          ['decrypt']
        );

        const decryptedData = await crypto.subtle.decrypt(
          { name: 'AES-GCM', iv: iv },
          key,
          encryptedData
        );

        const decoder = new TextDecoder();
        const jsonStr = decoder.decode(decryptedData);
        const importData = JSON.parse(jsonStr);
        
        processImportedData(importData);
      } else {
        // Plain JSON file
        const text = await file.text();
        const importData = JSON.parse(text);
        processImportedData(importData);
      }
    } catch (err) {
      setError(t('smartcard.importError') + ': ' + err.message);
    }

    event.target.value = '';
  };

  const processImportedData = (importData) => {
    if (importData.backups && Array.isArray(importData.backups)) {
      const newBackups = [...backups];
      let imported = 0;

      for (const backup of importData.backups) {
        const exists = newBackups.find(b => 
          b.serialNumber === backup.serialNumber && b.name === backup.name
        );
        if (!exists) {
          newBackups.push({
            ...backup,
            id: `backup-${Date.now()}-${imported}`,
            importedAt: new Date().toISOString()
          });
          imported++;
        }
      }

      saveBackups(newBackups);
      setSuccess(t('smartcard.importSuccess', { count: imported }));
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSuccess(t('smartcard.copied'));
    setTimeout(() => setSuccess(null), 2000);
  };

  return (
    <div className="smartcard-backup">
      <div className="smartcard-header">
        <div className="smartcard-tabs">
          <button 
            className={`tab-btn ${activeTab === 'cards' ? 'active' : ''}`}
            onClick={() => setActiveTab('cards')}
          >
            <FiCreditCard /> {t('smartcard.tabCards')}
          </button>
          <button 
            className={`tab-btn ${activeTab === 'backups' ? 'active' : ''}`}
            onClick={() => setActiveTab('backups')}
          >
            <FiDatabase /> {t('smartcard.tabBackups')}
            {backups.length > 0 && <span className="badge">{backups.length}</span>}
          </button>
        </div>
        <button className="btn-close" onClick={onClose}>
          <FiX />
        </button>
      </div>

      {/* Notifications */}
      {error && (
        <div className="notification error">
          <FiAlertTriangle /> {error}
          <button onClick={() => setError(null)}><FiX /></button>
        </div>
      )}
      {success && (
        <div className="notification success">
          <FiCheck /> {success}
          <button onClick={() => setSuccess(null)}><FiX /></button>
        </div>
      )}

      <div className="smartcard-content">
        {activeTab === 'cards' && (
          <div className="cards-tab">
            {/* Scan buttons */}
            <div className="scan-section">
              <h3><FiCpu /> {t('smartcard.detectDevices')}</h3>
              <p className="hint">{t('smartcard.detectHint')}</p>
              
              <div className="scan-buttons">
                <button 
                  className="btn-scan yubikey"
                  onClick={scanYubiKeys}
                  disabled={loading || !hasWebUSB}
                >
                  <FiKey />
                  {loading ? t('smartcard.scanning') : t('smartcard.scanYubiKey')}
                </button>
                
                <button 
                  className="btn-scan smartcard"
                  onClick={scanSmartCards}
                  disabled={loading || !hasSmartCard}
                >
                  <FiCreditCard />
                  {loading ? t('smartcard.scanning') : t('smartcard.scanSmartCard')}
                </button>

                <button 
                  className="btn-scan manual"
                  onClick={() => setShowAddManual(true)}
                >
                  <FiPlus />
                  {t('smartcard.addManual')}
                </button>
              </div>

              {!hasWebUSB && (
                <div className="api-warning">
                  <FiAlertTriangle /> {t('smartcard.webUsbNotSupported')}
                </div>
              )}
              {!hasSmartCard && (
                <div className="api-warning">
                  <FiInfo /> {t('smartcard.smartCardApiExperimental')}
                </div>
              )}
            </div>

            {/* Detected YubiKeys */}
            {yubikeys.length > 0 && (
              <div className="detected-devices">
                <h3><FiKey /> {t('smartcard.detectedYubiKeys')}</h3>
                <div className="device-list">
                  {yubikeys.map(device => (
                    <div key={device.id} className="device-card yubikey">
                      <div className="device-icon">
                        <FiKey />
                      </div>
                      <div className="device-info">
                        <h4>{device.name}</h4>
                        <p>{device.type}</p>
                        <span className="serial">S/N: {device.serialNumber}</span>
                      </div>
                      <div className="device-actions">
                        <button 
                          className="btn-backup"
                          onClick={() => createBackup(device)}
                        >
                          <FiSave /> {t('smartcard.backup')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detected Smart Cards */}
            {cards.length > 0 && (
              <div className="detected-devices">
                <h3><FiCreditCard /> {t('smartcard.detectedCards')}</h3>
                <div className="device-list">
                  {cards.map(card => (
                    <div key={card.id} className="device-card smartcard">
                      <div className="device-icon">
                        <FiCreditCard />
                      </div>
                      <div className="device-info">
                        <h4>{card.type}</h4>
                        <p>{card.reader}</p>
                        <span className="atr">ATR: {card.atr.substring(0, 20)}...</span>
                      </div>
                      <div className="device-actions">
                        <button 
                          className="btn-backup"
                          onClick={() => createBackup(card)}
                        >
                          <FiSave /> {t('smartcard.backup')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manual Add Modal */}
            {showAddManual && (
              <div className="manual-add-modal">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3><FiPlus /> {t('smartcard.addManualBackup')}</h3>
                    <button onClick={() => setShowAddManual(false)}><FiX /></button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label>{t('smartcard.deviceName')} *</label>
                      <input
                        type="text"
                        value={manualBackup.name}
                        onChange={(e) => setManualBackup({...manualBackup, name: e.target.value})}
                        placeholder={t('smartcard.deviceNamePlaceholder')}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('smartcard.deviceType')}</label>
                      <select
                        value={manualBackup.type}
                        onChange={(e) => setManualBackup({...manualBackup, type: e.target.value})}
                      >
                        <option value="smartcard">{t('smartcard.typeSmartCard')}</option>
                        <option value="yubikey">{t('smartcard.typeYubiKey')}</option>
                        <option value="security-key">{t('smartcard.typeSecurityKey')}</option>
                        <option value="hsm">{t('smartcard.typeHSM')}</option>
                        <option value="other">{t('smartcard.typeOther')}</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>{t('smartcard.serialNumber')}</label>
                      <input
                        type="text"
                        value={manualBackup.serialNumber}
                        onChange={(e) => setManualBackup({...manualBackup, serialNumber: e.target.value})}
                        placeholder={t('smartcard.serialNumberPlaceholder')}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('smartcard.issuer')}</label>
                      <input
                        type="text"
                        value={manualBackup.issuer}
                        onChange={(e) => setManualBackup({...manualBackup, issuer: e.target.value})}
                        placeholder={t('smartcard.issuerPlaceholder')}
                      />
                    </div>

                    <div className="form-group">
                      <label>{t('smartcard.notes')}</label>
                      <textarea
                        value={manualBackup.notes}
                        onChange={(e) => setManualBackup({...manualBackup, notes: e.target.value})}
                        placeholder={t('smartcard.notesPlaceholder')}
                        rows={3}
                      />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn-secondary" onClick={() => setShowAddManual(false)}>
                      {t('common.cancel')}
                    </button>
                    <button className="btn-primary" onClick={addManualBackup}>
                      <FiSave /> {t('smartcard.createBackup')}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Info section */}
            <div className="info-section">
              <h3><FiInfo /> {t('smartcard.aboutBackups')}</h3>
              <div className="info-content">
                <p>{t('smartcard.aboutBackupsDesc')}</p>
                <ul>
                  <li><FiCheck /> {t('smartcard.feature1')}</li>
                  <li><FiCheck /> {t('smartcard.feature2')}</li>
                  <li><FiCheck /> {t('smartcard.feature3')}</li>
                  <li><FiCheck /> {t('smartcard.feature4')}</li>
                </ul>
                <div className="security-note">
                  <FiLock />
                  <p>{t('smartcard.securityNote')}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'backups' && (
          <div className="backups-tab">
            {/* Import/Export buttons */}
            <div className="backup-actions">
              <button className="btn-action" onClick={exportBackups} disabled={backups.length === 0}>
                <FiDownload /> {t('smartcard.exportJson')}
              </button>
              <button className="btn-action encrypted" onClick={exportEncryptedBackups} disabled={backups.length === 0}>
                <FiLock /> {t('smartcard.exportEncrypted')}
              </button>
              <label className="btn-action import">
                <FiUpload /> {t('smartcard.import')}
                <input 
                  type="file" 
                  accept=".json,.enc" 
                  onChange={importBackups}
                  style={{ display: 'none' }}
                />
              </label>
            </div>

            {/* Backups list */}
            {backups.length === 0 ? (
              <div className="empty-state">
                <FiDatabase />
                <h3>{t('smartcard.noBackups')}</h3>
                <p>{t('smartcard.noBackupsDesc')}</p>
              </div>
            ) : (
              <div className="backups-list">
                {backups.map(backup => (
                  <BackupCard
                    key={backup.id}
                    backup={backup}
                    onDelete={() => deleteBackup(backup.id)}
                    onAddTotp={(totp) => addTotpToBackup(backup.id, totp)}
                    onAddCert={(cert) => addCertificateToBackup(backup.id, cert)}
                    onCopy={copyToClipboard}
                    onUpdate={(updated) => {
                      const newBackups = backups.map(b => 
                        b.id === backup.id ? { ...updated, updatedAt: new Date().toISOString() } : b
                      );
                      saveBackups(newBackups);
                    }}
                    t={t}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Backup Card Component
function BackupCard({ backup, onDelete, onAddTotp, onAddCert, onCopy, onUpdate, t }) {
  const [expanded, setExpanded] = useState(false);
  const [showAddTotp, setShowAddTotp] = useState(false);
  const [showAddCert, setShowAddCert] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState(backup.notes || '');
  const [totpForm, setTotpForm] = useState({ issuer: '', account: '', secret: '' });
  const [certForm, setCertForm] = useState({ name: '', data: '', type: 'x509' });

  const getTypeIcon = () => {
    switch (backup.type) {
      case 'yubikey': return <FiKey />;
      case 'security-key': return <FiShield />;
      case 'hsm': return <FiHardDrive />;
      default: return <FiCreditCard />;
    }
  };

  const handleAddTotp = () => {
    if (totpForm.secret) {
      onAddTotp({
        id: `totp-${Date.now()}`,
        ...totpForm,
        addedAt: new Date().toISOString()
      });
      setTotpForm({ issuer: '', account: '', secret: '' });
      setShowAddTotp(false);
    }
  };

  const handleAddCert = () => {
    if (certForm.data) {
      onAddCert({
        id: `cert-${Date.now()}`,
        ...certForm,
        addedAt: new Date().toISOString()
      });
      setCertForm({ name: '', data: '', type: 'x509' });
      setShowAddCert(false);
    }
  };

  const saveNotes = () => {
    onUpdate({ ...backup, notes });
    setEditingNotes(false);
  };

  return (
    <div className={`backup-card ${expanded ? 'expanded' : ''}`}>
      <div className="backup-header" onClick={() => setExpanded(!expanded)}>
        <div className="backup-icon">{getTypeIcon()}</div>
        <div className="backup-info">
          <h4>{backup.name}</h4>
          <span className="backup-type">{backup.productType || backup.type}</span>
          {backup.serialNumber && (
            <span className="backup-serial">S/N: {backup.serialNumber}</span>
          )}
        </div>
        <div className="backup-meta">
          <span className="backup-date">
            {new Date(backup.createdAt).toLocaleDateString()}
          </span>
          <span className={`expand-icon ${expanded ? 'expanded' : ''}`}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </div>

      {expanded && (
        <div className="backup-details">
          {/* Metadata */}
          <div className="detail-section">
            <h5>{t('smartcard.metadata')}</h5>
            <div className="metadata-grid">
              {backup.manufacturer && (
                <div className="metadata-item">
                  <span className="label">{t('smartcard.manufacturer')}</span>
                  <span className="value">{backup.manufacturer}</span>
                </div>
              )}
              {backup.serialNumber && (
                <div className="metadata-item">
                  <span className="label">{t('smartcard.serialNumber')}</span>
                  <span className="value">
                    {backup.serialNumber}
                    <button className="btn-copy" onClick={(e) => { e.stopPropagation(); onCopy(backup.serialNumber); }}>
                      <FiCopy />
                    </button>
                  </span>
                </div>
              )}
              {backup.issuer && (
                <div className="metadata-item">
                  <span className="label">{t('smartcard.issuer')}</span>
                  <span className="value">{backup.issuer}</span>
                </div>
              )}
            </div>
          </div>

          {/* TOTP Secrets */}
          <div className="detail-section">
            <div className="section-header">
              <h5><FiLock /> {t('smartcard.totpSecrets')}</h5>
              <button className="btn-add-small" onClick={() => setShowAddTotp(true)}>
                <FiPlus />
              </button>
            </div>
            {backup.totpSecrets?.length > 0 ? (
              <div className="secrets-list">
                {backup.totpSecrets.map((totp, idx) => (
                  <div key={idx} className="secret-item">
                    <span className="secret-issuer">{totp.issuer || 'Unknown'}</span>
                    <span className="secret-account">{totp.account}</span>
                    <button 
                      className="btn-copy" 
                      onClick={(e) => { e.stopPropagation(); onCopy(totp.secret); }}
                      title={t('smartcard.copySecret')}
                    >
                      <FiCopy />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-hint">{t('smartcard.noTotpSecrets')}</p>
            )}

            {showAddTotp && (
              <div className="add-form">
                <input
                  type="text"
                  placeholder={t('smartcard.issuer')}
                  value={totpForm.issuer}
                  onChange={(e) => setTotpForm({...totpForm, issuer: e.target.value})}
                />
                <input
                  type="text"
                  placeholder={t('smartcard.account')}
                  value={totpForm.account}
                  onChange={(e) => setTotpForm({...totpForm, account: e.target.value})}
                />
                <input
                  type="text"
                  placeholder={t('smartcard.secret')}
                  value={totpForm.secret}
                  onChange={(e) => setTotpForm({...totpForm, secret: e.target.value})}
                />
                <div className="form-actions">
                  <button className="btn-cancel" onClick={() => setShowAddTotp(false)}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleAddTotp}>
                    <FiSave /> {t('common.save')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Certificates */}
          <div className="detail-section">
            <div className="section-header">
              <h5><FiShield /> {t('smartcard.certificates')}</h5>
              <button className="btn-add-small" onClick={() => setShowAddCert(true)}>
                <FiPlus />
              </button>
            </div>
            {backup.certificates?.length > 0 ? (
              <div className="certs-list">
                {backup.certificates.map((cert, idx) => (
                  <div key={idx} className="cert-item">
                    <FiShield />
                    <span className="cert-name">{cert.name || `Certificate ${idx + 1}`}</span>
                    <span className="cert-type">{cert.type}</span>
                    <button 
                      className="btn-copy" 
                      onClick={(e) => { e.stopPropagation(); onCopy(cert.data); }}
                    >
                      <FiCopy />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="empty-hint">{t('smartcard.noCertificates')}</p>
            )}

            {showAddCert && (
              <div className="add-form">
                <input
                  type="text"
                  placeholder={t('smartcard.certName')}
                  value={certForm.name}
                  onChange={(e) => setCertForm({...certForm, name: e.target.value})}
                />
                <select
                  value={certForm.type}
                  onChange={(e) => setCertForm({...certForm, type: e.target.value})}
                >
                  <option value="x509">X.509</option>
                  <option value="pgp">PGP</option>
                  <option value="ssh">SSH</option>
                  <option value="other">{t('smartcard.typeOther')}</option>
                </select>
                <textarea
                  placeholder={t('smartcard.certData')}
                  value={certForm.data}
                  onChange={(e) => setCertForm({...certForm, data: e.target.value})}
                  rows={4}
                />
                <div className="form-actions">
                  <button className="btn-cancel" onClick={() => setShowAddCert(false)}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={handleAddCert}>
                    <FiSave /> {t('common.save')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="detail-section">
            <div className="section-header">
              <h5><FiEdit2 /> {t('smartcard.notes')}</h5>
              {!editingNotes && (
                <button className="btn-edit-small" onClick={() => setEditingNotes(true)}>
                  <FiEdit2 />
                </button>
              )}
            </div>
            {editingNotes ? (
              <div className="notes-edit">
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder={t('smartcard.notesPlaceholder')}
                />
                <div className="form-actions">
                  <button className="btn-cancel" onClick={() => { setNotes(backup.notes || ''); setEditingNotes(false); }}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn-save" onClick={saveNotes}>
                    <FiSave /> {t('common.save')}
                  </button>
                </div>
              </div>
            ) : (
              <p className="notes-content">{backup.notes || t('smartcard.noNotes')}</p>
            )}
          </div>

          {/* Actions */}
          <div className="backup-actions-footer">
            <button className="btn-danger" onClick={onDelete}>
              <FiTrash2 /> {t('common.delete')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SmartCardBackup;
